/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState } from 'react';
import { connect } from 'dva';
import { useIntl } from 'react-intl';
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import ProTable from '@src/packages/pro-table/Table';
import { ActionType } from '@src/packages/pro-table';
import VoucherReminderFormCtrl from '../schemaTemplate/VoucherReminderFormCtrl';
import VoucherReminderDetail from './VoucherReminderDetail';
import VoucherReminderAppliedVouchersModal from './VoucherReminderAppliedVouchersModal';
import voucherReminderApi from './voucherReminderApi';
import { collectCampaignUserIds } from './voucherReminderAppliedVouchers';
import { formatUserById, resolveUserLabels } from './voucherReminderUsers';
import {
  formatTs,
  getRejectReason,
  msToScheduleTime,
  parseIdList,
  statusMeta,
  triggerLabel,
  canUsePageButton,
  findPageButton,
  getPageButtonTitle,
  VR_BUTTON_MODE,
  VR_CHANNEL_OPTIONS,
  VR_STATUS,
  VR_STATUS_OPTIONS,
  VR_TRIGGER_OPTIONS,
} from './voucherReminderUtils';

const VoucherReminderListCtrl = (props: any) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);

  const actionRef = useRef<ActionType>();
  const [name, setName] = useState<string>('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [triggerType, setTriggerType] = useState<string | undefined>(undefined);
  const [channel, setChannel] = useState<string | undefined>(undefined);
  const [voucherId, setVoucherId] = useState<number | undefined>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editCampaign, setEditCampaign] = useState<any>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [userLabels, setUserLabels] = useState<Record<number, string>>({});
  const [voucherModal, setVoucherModal] = useState<{
    open: boolean;
    ids: unknown;
    title: string;
  }>({ open: false, ids: [], title: '' });

  const pageId = props.pageInfo?.id || props.query?.page;
  const roleId =
    props.authUser?.roleId != null ? Number(props.authUser.roleId) : null;

  const currentUserId =
    props.authUser?.id != null
      ? Number(props.authUser.id)
      : props.authUser != null && typeof props.authUser === 'number'
        ? Number(props.authUser)
        : null;

  const canCreate = canUsePageButton(
    props.pageInfo,
    VR_BUTTON_MODE.CREATE,
    roleId
  );

  const reload = () => actionRef.current?.reload();

  const openCreate = () => {
    setFormMode('create');
    setEditCampaign(null);
    setFormOpen(true);
  };

  const openEdit = (campaign: any) => {
    setFormMode('edit');
    setEditCampaign(campaign);
    setDetailId(null);
    setFormOpen(true);
  };

  const empty = t('voucherReminder.empty');
  const userLabel = (userId: any) => formatUserById(userId, userLabels, empty);

  const openVoucherQuickView = (row: any) => {
    const count = parseIdList(row?.voucherIds).length;
    setVoucherModal({
      open: true,
      ids: row?.voucherIds,
      title: t('voucherReminder.list.voucherListTitle', {
        name: row?.name || `#${row?.id}`,
        count,
      }),
    });
  };

  const columns: any[] = [
    {
      title: t('voucherReminder.col.id'),
      dataIndex: 'id',
      width: 70,
      search: false,
    },
    {
      title: t('voucherReminder.col.name'),
      dataIndex: 'name',
      width: 160,
      ellipsis: true,
      search: false,
    },
    {
      title: t('voucherReminder.col.status'),
      dataIndex: 'status',
      width: 130,
      search: false,
      render: (val: string, row: any) => {
        const m = statusMeta(val, intl);
        const rejectReason = getRejectReason(row);
        const tag = <Tag color={m.color}>{m.label}</Tag>;
        if (val !== VR_STATUS.REJECTED) return tag;
        return (
          <Space direction="vertical" size={2}>
            {rejectReason ? (
              <Tooltip title={rejectReason}>{tag}</Tooltip>
            ) : (
              tag
            )}
            {rejectReason ? (
              <Tooltip title={rejectReason}>
                <span
                  style={{
                    color: '#cf1322',
                    fontSize: 12,
                    maxWidth: 120,
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rejectReason}
                </span>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t('voucherReminder.col.createdBy'),
      dataIndex: 'createdBy',
      width: 140,
      search: false,
      // ProTable truyền ReactNode vào arg đầu khi ellipsis — luôn lấy từ row
      render: (_: any, row: any) => {
        const label = userLabel(row?.createdBy);
        return <Tooltip title={label}>{label}</Tooltip>;
      },
    },
    {
      title: t('voucherReminder.col.approvedBy'),
      dataIndex: 'approvedBy',
      width: 140,
      search: false,
      render: (_: any, row: any) => {
        const label = userLabel(row?.approvedBy);
        return label === empty ? (
          empty
        ) : (
          <Tooltip title={label}>{label}</Tooltip>
        );
      },
    },
    {
      title: t('voucherReminder.col.rejectedBy'),
      dataIndex: 'rejectedBy',
      width: 140,
      search: false,
      render: (_: any, row: any) => {
        const label = userLabel(row?.rejectedBy);
        return label === empty ? (
          empty
        ) : (
          <Tooltip title={label}>{label}</Tooltip>
        );
      },
    },
    {
      title: t('voucherReminder.col.trigger'),
      dataIndex: 'triggerType',
      width: 180,
      search: false,
      render: (_: any, row: any) =>
        `${triggerLabel(row.triggerType, intl)} (${row.triggerValue ?? 0})`,
    },
    {
      title: t('voucherReminder.col.schedule'),
      dataIndex: 'scheduleType',
      width: 130,
      search: false,
      render: (_: any, row: any) =>
        row.scheduleType === 'daily_at'
          ? t('voucherReminder.schedule.dailyAt', {
              time: msToScheduleTime(row.scheduleTime) || empty,
            })
          : row.scheduleType || empty,
    },
    {
      title: t('voucherReminder.col.vouchers'),
      dataIndex: 'voucherIds',
      width: 120,
      search: false,
      render: (val: any, row: any) => {
        const count = parseIdList(val).length;
        return (
          <Space size={4}>
            <span>{count}</span>
            {count > 0 && (
              <Tooltip title={t('voucherReminder.detail.viewVouchers')}>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => openVoucherQuickView(row)}
                  style={{ padding: 0, height: 'auto' }}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t('voucherReminder.col.startAt'),
      dataIndex: 'startAt',
      width: 150,
      search: false,
      render: (_: any, row: any) => (
        <Tooltip title={formatTs(row.startAt, intl.locale)}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatTs(row.startAt, intl.locale)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('voucherReminder.col.endAt'),
      dataIndex: 'endAt',
      width: 150,
      search: false,
      render: (_: any, row: any) => (
        <Tooltip title={formatTs(row.endAt, intl.locale)}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatTs(row.endAt, intl.locale)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('voucherReminder.col.lastRun'),
      dataIndex: 'lastRunAt',
      width: 140,
      search: false,
      render: (_: any, row: any) => formatTs(row?.lastRunAt, intl.locale),
    },
    {
      title: t('voucherReminder.col.actions'),
      valueType: 'option',
      width: 110,
      fixed: 'right',
      render: (_: any, row: any) => [
        <a key="view" onClick={() => setDetailId(row.id)}>
          <EyeOutlined /> {t('voucherReminder.action.detail')}
        </a>,
      ],
    },
  ];

  return (
    <div>
      <ProTable
        headerTitle={t('voucherReminder.title')}
        actionRef={actionRef as any}
        rowKey="id"
        search={false}
        options={{ reload: true, density: true, setting: true }}
        toolBarRender={() => [
          <Space key="filters" wrap>
            <Input
              allowClear
              placeholder={t('voucherReminder.search.name')}
              prefix={<SearchOutlined />}
              style={{ width: 180 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onPressEnter={() => reload()}
            />
            <Select
              allowClear
              placeholder={t('voucherReminder.search.status')}
              style={{ width: 140 }}
              value={status}
              onChange={(v) => {
                setStatus(v);
                setTimeout(() => reload(), 0);
              }}
            >
              {VR_STATUS_OPTIONS.map((s) => (
                <Select.Option key={s.value} value={s.value}>
                  {t(s.labelId)}
                </Select.Option>
              ))}
            </Select>
            <Select
              allowClear
              placeholder={t('voucherReminder.search.trigger')}
              style={{ width: 180 }}
              value={triggerType}
              onChange={(v) => {
                setTriggerType(v);
                setTimeout(() => reload(), 0);
              }}
            >
              {VR_TRIGGER_OPTIONS.map((s) => (
                <Select.Option key={s.value} value={s.value}>
                  {t(s.labelId)}
                </Select.Option>
              ))}
            </Select>
            <Select
              allowClear
              placeholder={t('voucherReminder.search.channel')}
              style={{ width: 130 }}
              value={channel}
              onChange={(v) => {
                setChannel(v);
                setTimeout(() => reload(), 0);
              }}
            >
              {VR_CHANNEL_OPTIONS.map((s) => (
                <Select.Option key={s.value} value={s.value}>
                  {t(s.labelId)}
                </Select.Option>
              ))}
            </Select>
            <InputNumber
              min={1}
              placeholder={t('voucherReminder.search.voucherId')}
              style={{ width: 130 }}
              value={voucherId}
              onChange={(v) =>
                setVoucherId(typeof v === 'number' ? v : undefined)
              }
            />
            <Button icon={<ReloadOutlined />} onClick={() => reload()}>
              {t('voucherReminder.action.filter')}
            </Button>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {getPageButtonTitle(
                  findPageButton(props.pageInfo, VR_BUTTON_MODE.CREATE),
                  intl.locale,
                  t('voucherReminder.action.create')
                )}
              </Button>
            )}
          </Space>,
        ]}
        request={async (params: any) => {
          const pageSize = params.pageSize || 20;
          const current = params.current || 1;
          const res = await voucherReminderApi.list(
            {
              skip: (current - 1) * pageSize,
              limit: pageSize,
              status: status || null,
              name: name || null,
              triggerType: triggerType || null,
              channel: channel || null,
              voucherId: voucherId || null,
            },
            { pageId, api: 'list' }
          );
          if (!res.ok) {
            setUserLabels({});
            return { data: [], success: false, total: 0 };
          }
          const data = res.data || [];
          const labels = await resolveUserLabels(
            collectCampaignUserIds(data),
            pageId
          );
          setUserLabels(labels);
          return {
            data,
            success: true,
            total: res.total || 0,
          };
        }}
        columns={columns}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
        scroll={{ x: 1800 }}
      />

      <VoucherReminderAppliedVouchersModal
        visible={voucherModal.open}
        onClose={() => setVoucherModal((s) => ({ ...s, open: false }))}
        voucherIds={voucherModal.ids}
        pageInfo={props.pageInfo}
        title={voucherModal.title}
      />

      <Modal
        title={
          formMode === 'edit'
            ? t('voucherReminder.drawer.edit')
            : t('voucherReminder.drawer.create')
        }
        visible={formOpen}
        onCancel={() => setFormOpen(false)}
        footer={null}
        width={1120}
        destroyOnClose
        maskClosable={false}
        centered
        bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}
      >
        <VoucherReminderFormCtrl
          mode={formMode}
          id={editCampaign?.id}
          initial={formMode === 'edit' ? editCampaign : undefined}
          pageInfo={props.pageInfo}
          onClose={() => setFormOpen(false)}
          onSuccess={() => reload()}
        />
      </Modal>

      <Modal
        title={t('voucherReminder.drawer.detail', { id: detailId || '' })}
        visible={detailId != null}
        onCancel={() => setDetailId(null)}
        footer={null}
        width={1120}
        destroyOnClose
        maskClosable={false}
        centered
        bodyStyle={{ paddingTop: 12, paddingBottom: 8 }}
      >
        {detailId != null && (
          <VoucherReminderDetail
            id={detailId}
            currentUserId={currentUserId}
            roleId={roleId}
            pageInfo={props.pageInfo}
            onClose={() => setDetailId(null)}
            onChanged={() => reload()}
            onEdit={(campaign) => {
              if (campaign?.status === VR_STATUS.REJECTED) {
                openEdit(campaign);
              }
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default connect(({ auth }: any) => ({
  authUser: auth?.authUser,
}))(VoucherReminderListCtrl);
